import { useCallback, useState } from 'react';
import { classes, ecrireStockage, lireStockage } from '../outils/classes';
import styles from './Filtres.module.css';

export interface GroupeFiltre {
  id: string;
  libelle: string;
  options: { valeur: string; libelle: string }[];
}

export type ValeursFiltres = Record<string, string>;

export interface ProprietesFiltres {
  groupes: GroupeFiltre[];
  valeurs: ValeursFiltres;
  onChanger: (valeurs: ValeursFiltres) => void;
  libelle?: string;
  className?: string;
}

/** Filtres en puces : un choix par groupe, état annoncé par aria-pressed (pas seulement la couleur). */
export function Filtres({ groupes, valeurs, onChanger, libelle = 'Filtres', className }: ProprietesFiltres) {
  return (
    <div className={classes(styles.filtres, className)} role="group" aria-label={libelle}>
      {groupes.map((g) => (
        <fieldset key={g.id} className={styles.groupe}>
          <legend className="cc-etiquette">{g.libelle}</legend>
          <div className={styles.options}>
            {g.options.map((o) => (
              <button
                key={o.valeur}
                type="button"
                className={styles.puce}
                aria-pressed={valeurs[g.id] === o.valeur}
                onClick={() => onChanger({ ...valeurs, [g.id]: o.valeur })}
              >
                {o.libelle}
              </button>
            ))}
          </div>
        </fieldset>
      ))}
    </div>
  );
}

/**
 * Filtres persistés : relus au retour sur l'écran. La persistance par défaut est locale à
 * l'appareil ; l'application peut fournir une fonction d'enregistrement côté serveur.
 */
export function useFiltresPersistes(cle: string, defaut: ValeursFiltres, enregistrer?: (v: ValeursFiltres) => void) {
  const [valeurs, setValeurs] = useState<ValeursFiltres>(() => ({ ...defaut, ...(lireStockage<ValeursFiltres>(`cc-filtres:${cle}`) ?? {}) }));
  const changer = useCallback(
    (v: ValeursFiltres) => {
      setValeurs(v);
      ecrireStockage(`cc-filtres:${cle}`, v);
      enregistrer?.(v);
    },
    [cle, enregistrer],
  );
  return [valeurs, changer] as const;
}
